import { i18n } from '@lingui/core';
import {
  detect,
  fromNavigator,
  fromStorage,
  fromUrl,
} from '@lingui/detect-locale';
import { I18nProvider } from '@lingui/react';
import { init as initApm } from '@elastic/apm-rum';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import { FlagsProvider } from 'react-feature-flags';
import '@fontsource/inter';

import { ErrorBoundary } from './common-components/composed/errorBoundary/ErrorBoundary';
import config from './config';
import {
  FDA_BUILD,
  NON_FDA_BUILD,
  SENTRY_DSN,
  SENTRY_TRACE_SAMPLE_RATE,
  SHOW_VALIDATION_DROP_DOWN_MENUS_RULE_NAME,
  ELASTIC_APM_DISTRIBUTED_TRACING_ORIGINS,
  ELASTIC_APM_SERVICE_NAME,
  ELASTIC_APM_SERVER_URL,
} from './constants';
import { messages as enMessages } from './locales/en/messages';
import { messages as frMessages } from './locales/fr-CA/messages';
import { messages as zuMessages } from './locales/zu/messages';
import Root from './root/Root';
import { configureStore, history } from './store/configureStore';
import { initializeApmClickTracking } from './utils/apmClickTracking';
import { initializeApmFilters } from './utils/apmFilters';

import './app.global.css';
import '@repo/videa-components/index.css';

const DEFAULT_FALLBACK = () => 'en';
const VALID_LOCALES = ['en', 'zu', 'zu-ZA'] as const;
let result = detect(
  fromUrl('lang'),
  fromStorage('lang'),
  fromNavigator(),
  DEFAULT_FALLBACK,
);

// If the detected locale is not in the list of valid locales, set it to en, otherwise we will see invalid strings in the UI
if (!VALID_LOCALES.includes(result as (typeof VALID_LOCALES)[number])) {
  result = 'en';
}

i18n.load({
  en: enMessages,
  'fr-CA': frMessages,
  'zu-ZA': frMessages,
  fr: frMessages,
  zu: zuMessages,
});
// Remove all quotes from the user's selectedLocale
const selectedLocale = localStorage
  .getItem('jotai-selectedLocale')
  ?.replaceAll('"', '');
// Default to the user's selectedLocale, otherwise default to the detected locale, otherwise default to en
i18n.activate(selectedLocale || result || 'en');

let apm;
if (config.ELASTIC_APM_ENABLED) {
  try {
    apm = initApm({
      serviceName: ELASTIC_APM_SERVICE_NAME,
      serverUrl: ELASTIC_APM_SERVER_URL,
      environment: config.ELASTIC_APM_ENVIRONMENT,
      distributedTracingOrigins: ELASTIC_APM_DISTRIBUTED_TRACING_ORIGINS,
      transactionSampleRate: 0.1,
      propagateTracestate: true,
      breakdownMetrics: true
    });

    // Store APM instance for use throughout the app
    initializeApmFilters(apm);
    initializeApmClickTracking();
  } catch (error) {
    console.error('[APM] Failed to initialize:', error);
  }
}

const { BUILD_CONFIGURATION, SHOW_VALIDATION_DROP_DOWN_MENUS } = config;
const ROOT_ID = 'root';
const flags = [
  { name: FDA_BUILD, isActive: BUILD_CONFIGURATION === 'FDA' },
  { name: NON_FDA_BUILD, isActive: BUILD_CONFIGURATION === 'NON_FDA' },
  {
    name: SHOW_VALIDATION_DROP_DOWN_MENUS_RULE_NAME,
    isActive: SHOW_VALIDATION_DROP_DOWN_MENUS,
  },
];

if (config.SENTRY_ENABLED && window.location.hostname !== 'localhost') {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        unmask: ['.sentry-unmask'],
        unblock: ['.sentry-unmask'],
      }),
      Sentry.rewriteFramesIntegration({
        iteratee: (frame) => {
          // frames should be relative to the root of the project (remove domain, etc)
          return {
            ...frame,
            filename: frame.filename?.replace(/^.*?\/src\//, '~/src/'),
          };
        },
      }),
    ],
    beforeSend(event, hint) {
      // Filter out specific Userpilot SDK error that occurs when switching
      // between FMX and single image view and causes Userpilot items to disappear.
      // Error: https://videahealth.sentry.io/issues/6791454260/?project=1799723&query=is%3Aunresolved&referrer=issue-stream&sort=freq
      const exception = event.exception?.values?.[0];
      const frames = exception?.stacktrace?.frames ?? [];
      const stack = (hint.originalException as Error)?.stack;
      const message = exception?.value ?? '';

      const isFileMatch = (filename: string) =>
        frames.some((frame) =>
          frame.filename?.toLowerCase().includes(filename),
        ) || stack?.toLowerCase().includes(filename);

      const isUserpilot = isFileMatch('userpilot.io/sdk');
      const isTypeError = exception?.type === 'TypeError';
      const isDisplayError = message.includes(
        "Cannot read properties of undefined (reading 'display')",
      );

      if (isUserpilot && isTypeError && isDisplayError) {
        return null;
      }

      // Filter out all errors from cornerstonejs/core package in node_modules
      const isCornerstoneCore = isFileMatch('@cornerstonejs/core');

      if (isCornerstoneCore) {
        return null;
      }

      return event;
    },
    tracesSampleRate: SENTRY_TRACE_SAMPLE_RATE,
    environment: process.env.VITE_ENV,
    release: (process.env.CIRCLE_SHA1 || '').slice(0, 12), // get first 12 characters of commit hash
    dist: process.env.VITE_ENV, // to differentiate between environments
    replaysOnErrorSampleRate: 1,
    normalizeDepth: 10,
  });
}

const AppContainer = () => {
  // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
  const store = configureStore();

  return (
    <FlagsProvider value={flags}>
      <Root store={store} history={history} />
    </FlagsProvider>
  );
};

window.localStorage.removeItem('okta-cache-storage');
const domNode = document.getElementById(ROOT_ID);
// @ts-expect-error TS(2345): Argument of type 'HTMLElement | null' is not assig... Remove this comment to see the full error message
const root = createRoot(domNode);

root.render(
  <I18nProvider i18n={i18n}>
    <ErrorBoundary>
      <AppContainer />
    </ErrorBoundary>
  </I18nProvider>,
);
